import { VerifyResponse } from "@/services/onboarding";
import { checkSectionOfInstrument } from "@/services/sectionOfInstrument";

import { auth } from "@/lib/firebase";
import { PROFILE_URL } from "@/shared/environmentVariables";
import UserService from "@/services/user";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { signInWithCustomToken } from "firebase/auth";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import AuthWrapper from "./AuthWrapper";

function redirectAfterOnboarding(redirectPath?: string | null) {
  const url = new URL(PROFILE_URL);
  if (redirectPath) {
    url.pathname = url.pathname.replace(/\/$/, "") + redirectPath;
  } else {
    url.pathname = url.pathname.replace(/\/$/, "") + "/new-tools";
    url.searchParams.set("sidebarOpen", "false");
  }
  window.location.href = url.href;
}

async function signInAndRedirect(
  customToken: string,
  redirectPath?: string | null,
) {
  const userCredential = await signInWithCustomToken(auth, customToken); // get token ID for user
  const idToken = await userCredential.user.getIdToken();
  await UserService.login({ idToken });
  auth.signOut();
  redirectAfterOnboarding(redirectPath);
}

/**
 * Загружает раздел - new-tools,
 * exemple:
 * http://localhost:3100/instrument?token=...
 *
 */
export default function SectionOfInstrument() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<VerifyResponse | null>(null);

  useEffect(() => {
    if (!token) {
      setError(t("onboarding_invalidLink"));
      setLoading(false);
      return;
    }

    UserService.logout()
      .then(() => checkSectionOfInstrument(token))
      .then(async (result) => {
        if (!result.valid) {
          setError(t("onboarding_linkNotFound"));
          setLoading(false);
          return;
        }

        // Return visit — auto-login without showing landing page
        if (result.alreadyOnboarded && result.customToken) {
          try {
            await signInAndRedirect(result.customToken, result.redirectPath);
          } catch {
            setError(t("onboarding_activationError"));
            setLoading(false);
          }
          return;
        }

        setData(result);
        setLoading(false);
      })
      .catch(() => {
        setError(t("onboarding_verificationError"));
        setLoading(false);
      });
  }, [token, t]);

  // Verify loading or return-visit auto-login in progress
  if (loading) {
    return (
      <AuthWrapper isOnboarding={true}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 300,
          }}
        >
          <CircularProgress />
        </Box>
      </AuthWrapper>
    );
  }

  // Error state
  if (error) {
    return (
      <AuthWrapper isOnboarding={true}>
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        </Box>
      </AuthWrapper>
    );
  }

  return (
    <AuthWrapper isOnboarding={true}>
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
        }}
      >
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5 }}>
            {t("onboarding_title")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {data?.companyName}
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{ py: 1.5 }}
            // onClick={handleActivate}
          >
            {t("onboarding_proceed")}
          </Button>
        </Box>
      </Box>
    </AuthWrapper>
  );
}
